/**
 * Navegação do agendamento em etapas.
 *
 * A etapa atual é derivada, não guardada de forma independente: o hook guarda a
 * etapa *pedida* e recorta pela primeira etapa incompleta a cada render. Assim,
 * voltar e trocar o serviço não deixa o cliente parado numa etapa que não vale
 * mais.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  buildBookingWizardSteps,
  clampBookingStepIndex,
  findBookingStepIndex,
  getBookingStepBlockedReason,
  getBookingWizardProgress,
  getMaxReachableStepIndex,
  isBookingStepComplete,
  type BookingWizardContext,
  type BookingWizardStep,
  type BookingWizardStepId,
} from "../utils/bookingWizard";

/**
 * PUB-13: cada etapa vira uma entrada do histórico do navegador. Sem isso, o
 * voltar do navegador (ou o gesto de voltar do Android) saía do wizard e
 * descartava a seleção. A entrada guarda só o id da etapa; a seleção continua
 * no estado da página, que não é desmontada.
 */
const HISTORY_STATE_KEY = "agilizaBookingStep";

const KNOWN_STEP_IDS: ReadonlySet<string> = new Set<BookingWizardStepId>([
  "service",
  "provider",
  "schedule",
  "showcase",
  "account",
  "details",
]);

function readHistoryStepId(state: unknown): BookingWizardStepId | null {
  if (!state || typeof state !== "object") return null;
  const value = (state as Record<string, unknown>)[HISTORY_STATE_KEY];
  return typeof value === "string" && KNOWN_STEP_IDS.has(value)
    ? (value as BookingWizardStepId)
    : null;
}

function scrollToTop() {
  if (typeof window !== "undefined") {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
}

export interface UseBookingWizardResult {
  steps: BookingWizardStep[];
  currentStep: BookingWizardStep;
  currentStepId: BookingWizardStepId;
  currentIndex: number;
  maxReachableIndex: number;
  progress: { current: number; total: number; percent: number };
  canAdvance: boolean;
  blockedReason: string | null;
  isFirstStep: boolean;
  isLastStep: boolean;
  goNext: () => void;
  goBack: () => void;
  goToStep: (stepId: BookingWizardStepId) => void;
  isStepComplete: (stepId: BookingWizardStepId) => boolean;
}

export function useBookingWizard(context: BookingWizardContext): UseBookingWizardResult {
  // Sem etapa pedida ainda: `findBookingStepIndex` devolve -1 e o hook resolve
  // para o índice 0, seja qual for a primeira etapa (varia se há profissionais
  // cadastrados). Fixar um id aqui faria a página pular direto para aquela
  // etapa ao carregar, ignorando as anteriores.
  const [requestedStepId, setRequestedStepId] = useState<BookingWizardStepId | null>(
    null
  );

  const steps = useMemo(
    () =>
      buildBookingWizardSteps({
        hasSelectedService: context.hasSelectedService,
        allowAnyProvider: context.allowAnyProvider,
        hasProviderOptions: context.hasProviderOptions,
        hasSelectedProvider: context.hasSelectedProvider,
        hasSelectedDate: context.hasSelectedDate,
        hasSelectedTime: context.hasSelectedTime,
        hasContactData: context.hasContactData,
        hasShowcaseProducts: context.hasShowcaseProducts,
        isCustomerLoggedIn: context.isCustomerLoggedIn,
      }),
    [
      context.hasSelectedService,
      context.allowAnyProvider,
      context.hasProviderOptions,
      context.hasSelectedProvider,
      context.hasSelectedDate,
      context.hasSelectedTime,
      context.hasContactData,
      context.hasShowcaseProducts,
      context.isCustomerLoggedIn,
    ]
  );

  const requestedIndex = findBookingStepIndex(steps, requestedStepId);
  const currentIndex = clampBookingStepIndex(
    requestedIndex === -1 ? 0 : requestedIndex,
    steps,
    context
  );
  const currentStep = steps[currentIndex] ?? steps[0];
  const maxReachableIndex = getMaxReachableStepIndex(steps, context);

  const currentStepId = currentStep.id;

  // Voltar/avançar do navegador: a entrada sem etapa é a de quem abriu o
  // wizard, ou seja, a primeira etapa. O recorte pela primeira etapa
  // incompleta continua valendo.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePopState = (event: PopStateEvent) => {
      setRequestedStepId(readHistoryStepId(event.state));
      scrollToTop();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  const goToStep = useCallback(
    (stepId: BookingWizardStepId) => {
      setRequestedStepId(stepId);
      if (typeof window !== "undefined" && stepId !== currentStepId) {
        // O Next copia o estado interno dele para a entrada nova; a URL fica.
        window.history.pushState({ [HISTORY_STATE_KEY]: stepId }, "");
      }
      scrollToTop();
    },
    [currentStepId]
  );

  const goNext = useCallback(() => {
    const next = steps[currentIndex + 1];
    if (!next) return;
    if (!isBookingStepComplete(currentStep.id, context)) return;
    goToStep(next.id);
  }, [steps, currentIndex, currentStep, context, goToStep]);

  const goBack = useCallback(() => {
    const previous = steps[currentIndex - 1];
    if (!previous) return;
    // Entrada criada pelo wizard: voltar pelo histórico mantém o botão da tela
    // e o do navegador em sincronia (o popstate aplica a etapa).
    if (
      typeof window !== "undefined" &&
      readHistoryStepId(window.history.state) !== null
    ) {
      window.history.back();
      return;
    }
    setRequestedStepId(previous.id);
    scrollToTop();
  }, [steps, currentIndex]);

  return {
    steps,
    currentStep,
    currentStepId,
    currentIndex,
    maxReachableIndex,
    progress: getBookingWizardProgress(steps, currentIndex),
    canAdvance: isBookingStepComplete(currentStep.id, context),
    blockedReason: getBookingStepBlockedReason(currentStep.id, context),
    isFirstStep: currentIndex === 0,
    isLastStep: currentIndex === steps.length - 1,
    goNext,
    goBack,
    goToStep,
    isStepComplete: (stepId: BookingWizardStepId) =>
      isBookingStepComplete(stepId, context),
  };
}
